<script lang="ts" setup>
	const props = defineProps<{
		isPending: boolean
		planDetails?: PolicyResponse | null
	}>()

	const { productCategoryType } = useUtils()
	const activePlan = computed(() => {
		return props.planDetails
	})

	defineEmits<{
		(event: 'activate'): void
		(event: 'reminder'): void
	}>()
	const categoryType = computed(() =>
		productCategoryType(
			props.planDetails?.product?.productCategory?.name || '',
			props.planDetails?.product?.code_name
		)
	)
	const { formatDate } = useCustomDate()
	const inspectionDetails = computed(() => {
		let labelKey = 'Vehicle inspected by'
		let inspectionDate = activePlan.value?.meta?.vehicleInspectionMeta?.timestamp || '---'
		if (categoryType.value?.isGadget) {
			labelKey = 'Gadget inspected by'
			inspectionDate =
				formatDate(activePlan.value?.meta?.gadgetInspectionMeta?.timestamp ?? '', 'DD/MMM/YYYY') || '---'
		}

		return {
			info: {
				[labelKey]: `${activePlan.value?.first_name || ''} ${activePlan.value?.last_name || ''}`,
				'Inspection date': inspectionDate
			},
			vehicleDetails: {
				'Left side view': activePlan.value?.meta?.vehicleInspectionMeta?.left_side.img_url,
				'Front view': activePlan.value?.meta?.vehicleInspectionMeta?.front_side.img_url,
				'Right side view': activePlan.value?.meta?.vehicleInspectionMeta?.right_side.img_url,
				'Back view': activePlan.value?.meta?.vehicleInspectionMeta?.back_side.img_url,
				'Chassis number': activePlan.value?.meta?.vehicleInspectionMeta?.chassis_number.img_url,
				'Dashboard view': activePlan.value?.meta?.vehicleInspectionMeta?.dashboard.img_url,
				'Interior view': activePlan.value?.meta?.vehicleInspectionMeta?.interior.img_url
			},
			gadgetDetails: {
				'Back view': activePlan.value?.meta?.gadgetInspectionMeta?.back_image,
				'Front view': activePlan.value?.meta?.gadgetInspectionMeta?.front_image,
				'serial number view': activePlan.value?.meta?.gadgetInspectionMeta?.serial_number_image,
				'Side view': activePlan.value?.meta?.gadgetInspectionMeta?.side_image
			}
		}
	})
	const isVehicleMetaEmpty = computed(() =>
		Object.values(inspectionDetails.value.vehicleDetails).every(item => !item)
	)
	const isImagesEmpty = computed(() => {
		return isVehicleMetaEmpty.value && Object.values(inspectionDetails.value.gadgetDetails).every(item => !item)
	})

	const pendingPolicyPlaceholder = computed(() => {
		return `This ${categoryType.value?.isAuto ? 'Vehicle' : 'Gadget'} has not been
									inspected yet.
	    Inspect your ${categoryType.value?.isAuto ? 'Vehicle' : 'Gadget'} to enjoy full
	    benefits.`
	})
	const imageDetails = computed(() => {
		if (isImagesEmpty.value) {
			return null
		}
		return (
			(!isVehicleMetaEmpty.value && inspectionDetails.value.vehicleDetails) ||
			inspectionDetails.value.gadgetDetails
		)
	})
</script>

<template>
	<div>
		<my-cover-pending-policy-placeholder
			v-if="isPending || isImagesEmpty"
			icon-dir="other"
			title="No Inspection Done Yet"
			:sub-title="pendingPolicyPlaceholder"
			icon-name="search"
			:is-pending="isPending || !activePlan?.meta?.inspectionLink || isImagesEmpty"
			use-reminder
			@activate="$emit('activate')"
			@reminder="$emit('reminder')"
		>
			<template v-if="(!activePlan?.meta?.inspectionLink && !isPending) || isImagesEmpty" #button-group>
				<div></div>
			</template>
		</my-cover-pending-policy-placeholder>

		<template v-else>
			<my-cover-policy-label
				:label="`Inspection details on this ${categoryType?.isAuto ? 'vehicle' : 'gadget'}`"
				:icon="{ name: 'bold-note', dir: 'documents' }"
			/>
			<div class="mt-10 min-h-[450px] w-full rounded border px-4 py-5">
				<div class="mb-5 flex flex-wrap items-center justify-between border-b py-4">
					<div v-for="(val, k) in inspectionDetails.info" :key="k" class="tracking-wide">
						<span class="text-xs text-gray-500">{{ k }}</span>
						<h5 class="text-sm font-medium text-gray-600">
							{{ val || 'N/A' }}
						</h5>
					</div>
				</div>
				<div
					class="mx-auto w-full max-w-[226px] rounded border border-purple-400 bg-purple-100 px-3 py-2 text-center text-sm font-medium tracking-wide text-purple-400"
				>
					Inspected Images
				</div>

				<div class="mt-8 grid grid-cols-2 gap-x-4 gap-y-5">
					<div v-for="(src, k) in imageDetails" :key="k" class="flex flex-col gap-y-2">
						<div class="mb-1 min-h-[70px] sm:min-w-[147px]">
							<img
								:src="src"
								class="w-full bg-gray-100 object-cover text-xs"
								:alt="k"
								:height="147"
								:width="147"
								loading="lazy"
							/>
						</div>
						<div
							class="mx-auto inline-flex justify-center rounded border border-purple-300 px-3 py-1 text-center text-xs font-medium tracking-wide text-purple-500"
						>
							{{ k }}
						</div>
					</div>
				</div>
			</div>
		</template>
	</div>
</template>
